'use client'

import React from 'react'
import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { Calendar, ClipboardList, CalendarCheck } from 'lucide-react'
import { cn, buildRegionUrl } from '@/shared/lib/utils'
import { useRegion } from '@/shared/hooks/use-region'
import {
  SEARCH_PARAMS_TAB_KEY,
  WORK_ORDER_TAB_TODAY,
  WORK_ORDER_TAB_JOB_BOARD,
  WORK_ORDER_TAB_LIST,
  WORK_ORDER_TAB_LINKS,
  type WorkOrderTabValue,
} from '../constants/work-order-tab-links'
import { useFeatureFlag } from '@/features/feature-flags/hooks/use-feature-flag'

interface WorkOrderTabsProps {
  tab?: WorkOrderTabValue
}

const tabIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  [WORK_ORDER_TAB_TODAY]: CalendarCheck,
  [WORK_ORDER_TAB_JOB_BOARD]: Calendar,
  [WORK_ORDER_TAB_LIST]: ClipboardList,
}

export function WorkOrderTabs({ tab }: WorkOrderTabsProps) {
  const { regionSlug } = useRegion()
  const searchParams = useSearchParams()
  const isJobBoardEnabled = useFeatureFlag('job_board')
  const defaultTab: WorkOrderTabValue = isJobBoardEnabled
    ? WORK_ORDER_TAB_JOB_BOARD
    : WORK_ORDER_TAB_TODAY

  const visibleLinks = WORK_ORDER_TAB_LINKS.filter(
    (link) => link.value !== WORK_ORDER_TAB_JOB_BOARD || isJobBoardEnabled,
  )

  const buildTabParams = (tabValue: string) => {
    const params: Record<string, string> = { [SEARCH_PARAMS_TAB_KEY]: tabValue }
    searchParams.forEach((value, key) => {
      if (key !== SEARCH_PARAMS_TAB_KEY) {
        params[key] = value
      }
    })
    return params
  }

  return (
    <div className="flex items-center justify-center gap-6 sm:gap-5 flex-wrap">
      {visibleLinks.map((link) => {
        const params = buildTabParams(link.value)
        const href = regionSlug
          ? buildRegionUrl(regionSlug, '/work-orders', params)
          : `/work-orders?${new URLSearchParams(params as Record<string, string>).toString()}`
        const Icon = tabIcons[link.value]
        const isActive = tab === link.value || (!tab && defaultTab === link.value)

        return (
          <Link
            key={link.value}
            href={href}
            className={cn(
              'flex items-center justify-center gap-1.5 sm:w-[110px] pb-0.5 border-b-[3px] border-transparent transition-colors px-2 sm:px-0 py-1 sm:py-0',
              isActive && 'border-primary text-primary font-medium'
            )}
            title={link.label}
          >
            {Icon && <Icon className="h-4 w-4" />}
            <span className="text-center text-sm">{link.label}</span>
          </Link>
        )
      })}
    </div>
  )
}

export default WorkOrderTabs
